import { useEffect } from "react";
import { useAtomValue } from "jotai";
import { Button } from "@common/Button";
import { ComponentColor } from "@common/types";
import { useLegalClassRestrictionApi } from "@/hooks/apiHooks/useLegalClassRestrictionApi";
import { isProcessingLegalClassRestrictionsAtom } from "@/store/legalClassRestrictionAtoms";
import type { LegalClassRestriction } from "@models/legalClassRestriction";
import { legalClassLabel } from "@models/product";
import {
    describeDeleteImpact,
    describeDestination,
} from "../_helpers/restrictionStatus";

const DIALOG_ID = "deleteLegalClassRestrictionModal";

const dialogElement = (): HTMLDialogElement | null => {
    const element = document.getElementById(DIALOG_ID);
    return element instanceof HTMLDialogElement ? element : null;
};

interface DeleteRestrictionDialogProps {
    restriction: LegalClassRestriction;
    /**
     * The screen's clock, passed in rather than read here. A dialog that read
     * `new Date()` at mount could land on the far side of a lifecycle boundary
     * from the badge the admin just clicked, so the confirmation would
     * contradict the row that prompted it.
     */
    now: Date;
    /** Called after the dialog's native close steps have run. */
    onClose: () => void;
    /** Called on a successful delete, before the dialog closes. */
    onDeleted: () => void;
}

/**
 * Deleting is a hard delete — the API removes the row rather than end-dating it
 * — so the confirmation states what this particular rule does *today* rather
 * than a generic warning. `describeDeleteImpact` derives that from the rule's
 * position in its effective window and from `isLegal`.
 */
export const DeleteRestrictionDialog = ({
    restriction,
    now,
    onClose,
    onDeleted,
}: DeleteRestrictionDialogProps) => {
    const { deleteLegalClassRestriction } = useLegalClassRestrictionApi();
    const isProcessing = useAtomValue(isProcessingLegalClassRestrictionsAtom);

    useEffect(() => {
        const dialog = dialogElement();
        dialog?.showModal();
        // The native focusing steps land on the first focusable (Keep, which
        // comes first). Focus it explicitly as well, so a stray Enter on open
        // never deletes (#452). Not React's autoFocus: React focuses at commit
        // without writing the attribute, and showModal then moves focus again.
        dialog
            ?.querySelector<HTMLElement>("[data-dialog-initial-focus] button")
            ?.focus();
    }, []);

    const handleDelete = async () => {
        try {
            await deleteLegalClassRestriction(restriction.id);

            onDeleted();
            dialogElement()?.close();
        } catch {
            // useApi already toasted; keep the dialog open so the admin can
            // retry or cancel rather than being left unsure whether it applied.
        }
    };

    const summary = `${legalClassLabel(restriction.legalClass)} in ${describeDestination(restriction)}`;

    return (
        <dialog
            id={DIALOG_ID}
            className="modal text-neutral"
            onCancel={(e) => {
                if (isProcessing) {
                    e.preventDefault();
                }
            }}
            onClose={onClose}
        >
            <div
                className="modal-box max-w-lg"
                role="alertdialog"
                aria-label={`Delete restriction: ${summary}`}
            >
                <h1 className="font-bold uppercase text-2xl">
                    Delete restriction
                </h1>
                <div className="divider divider-info mt-0"></div>
                <p className="font-semibold">{summary}</p>
                <p className="mt-2">
                    {describeDeleteImpact(restriction, now)}
                </p>
                <p className="mt-2 text-sm text-gray-500">
                    This removes the row outright. To retire a rule while
                    keeping the record of when it applied, set its end date
                    instead.
                </p>
                {/* The safe back-out comes first and is marked, so it is
                    where focus lands on open (#452). */}
                <div className="modal-action">
                    <span data-dialog-initial-focus="" className="contents">
                        <Button
                            color={ComponentColor.Neutral}
                            loading={isProcessing}
                            onClick={() => dialogElement()?.close()}
                        >
                            Keep restriction
                        </Button>
                    </span>
                    <Button
                        color={ComponentColor.Error}
                        loading={isProcessing}
                        onClick={handleDelete}
                    >
                        {isProcessing ? "Deleting..." : "Delete"}
                    </Button>
                </div>
            </div>
            {!isProcessing && (
                <form method="dialog" className="modal-backdrop">
                    <button>close</button>
                </form>
            )}
        </dialog>
    );
};
